import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import AuthModal from '../../../components/feature/AuthModal';
import CelebrationOverlay from '../../../components/base/CelebrationOverlay';
import {
  StudyGoal,
  GoalType,
  fetchGoalsByRange,
  fetchGoalsByType,
  addGoal,
  toggleGoalCompletion,
  deleteGoal,
  updateGoal,
} from '../../../hooks/useStudyGoals';
import { exportScheduleToPDF, ScheduleExportData } from '../../../utils/scheduleExport';

/* -------------------------------------------------
   Constants & Helpers (must be defined before use)
--------------------------------------------------- */
function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const wd = weekdays[d.getDay()];
  return `${month}/${day}（${wd}）`;
}

function isToday(dateStr: string): boolean {
  return dateStr === formatDateKey(new Date());
}

function isPast(dateStr: string): boolean {
  const today = formatDateKey(new Date());
  return dateStr < today;
}

type ScheduleTab = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
type SortMode = 'created' | 'difficulty_desc' | 'difficulty_asc';

const TAB_CONFIG: { key: ScheduleTab; label: string; icon: string }[] = [
  { key: 'daily', label: '日別', icon: 'ri-calendar-line' },
  { key: 'weekly', label: '週間目標', icon: 'ri-calendar-2-line' },
  { key: 'monthly', label: '月間目標', icon: 'ri-calendar-check-line' },
  { key: 'quarterly', label: '3ヶ月目標', icon: 'ri-calendar-todo-line' },
  { key: 'yearly', label: '年間目標', icon: 'ri-calendar-event-line' },
];

const SORT_OPTIONS: { key: SortMode; label: string; icon: string }[] = [
  { key: 'created', label: '追加順', icon: 'ri-sort-asc' },
  { key: 'difficulty_desc', label: '難易度 高→低', icon: 'ri-arrow-down-line' },
  { key: 'difficulty_asc', label: '難易度 低→高', icon: 'ri-arrow-up-line' },
];

function sortGoals(goals: StudyGoal[], mode: SortMode): StudyGoal[] {
  const sorted = [...goals];
  switch (mode) {
    case 'difficulty_desc':
      sorted.sort((a, b) => (b.difficulty || 3) - (a.difficulty || 3));
      break;
    case 'difficulty_asc':
      sorted.sort((a, b) => (a.difficulty || 3) - (b.difficulty || 3));
      break;
    case 'created':
    default:
      sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      break;
  }
  return sorted;
}

/* Period panel label configuration – placed before component so it can be used safely */
const PERIOD_LABELS: Record<
  ScheduleTab,
  { title: string; description: string; icon: string; color: string }
> = {
  weekly: {
    title: '週間目標',
    description: '今週中に達成したい目標を設定しましょう',
    icon: 'ri-calendar-2-line',
    color: 'from-sky-400 to-cyan-500',
  },
  monthly: {
    title: '月間目標',
    description: '今月中に達成したい目標を設定しましょう',
    icon: 'ri-calendar-check-line',
    color: 'from-violet-400 to-fuchsia-500',
  },
  quarterly: {
    title: '3ヶ月目標',
    description: '3ヶ月以内に達成したい中期目標を設定しましょう',
    icon: 'ri-calendar-todo-line',
    color: 'from-rose-400 to-pink-500',
  },
  yearly: {
    title: '年間目標',
    description: '今年中に達成したい長期目標を設定しましょう',
    icon: 'ri-calendar-event-line',
    color: 'from-amber-400 to-orange-500',
  },
  // dummy entry for type completeness
  daily: {
    title: '',
    description: '',
    icon: '',
    color: '',
  },
};

/* -------------------------------------------------
   Main Component
--------------------------------------------------- */
export default function StudySchedule({ layoutMode = 'normal' }: { layoutMode?: 'normal' | 'compact' }) {
  const { user, username, loading: authLoading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [activeTab, setActiveTab] = useState<ScheduleTab>('daily');

  // PDF export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMsg, setCelebrationMsg] = useState('');

  // Daily state
  const [goals, setGoals] = useState<StudyGoal[]>([]);
  const [loading, setLoading] = useState(false);
  const [newGoalText, setNewGoalText] = useState('');
  const [newGoalDifficulty, setNewGoalDifficulty] = useState(3);
  const [addingDate, setAddingDate] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  // Sort state
  const [dailySortMode, setDailySortMode] = useState<SortMode>('created');
  const [periodSortMode, setPeriodSortMode] = useState<SortMode>('created');

  // Edit state for daily goals
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [editingDifficulty, setEditingDifficulty] = useState(3);

  // Period goals state
  const [periodGoals, setPeriodGoals] = useState<StudyGoal[]>([]);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodNewGoalText, setPeriodNewGoalText] = useState('');
  const [showPeriodAdd, setShowPeriodAdd] = useState(false);
  const [periodNewDifficulty, setPeriodNewDifficulty] = useState(3);

  // Period offset states
  const [periodWeekOffset, setPeriodWeekOffset] = useState(0);
  const [periodMonthOffset, setPeriodMonthOffset] = useState(0);
  const [periodQuarterOffset, setPeriodQuarterOffset] = useState(0);
  const [periodYearOffset, setPeriodYearOffset] = useState(0);

  const weekDates = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7) + weekOffset * 7);
    const dates: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      dates.push(formatDateKey(d));
    }
    return dates;
  }, [weekOffset]);

  const weekLabel = useMemo(() => {
    if (weekDates.length === 0) return '';
    const start = new Date(weekDates[0] + 'T00:00:00');
    const end = new Date(weekDates[6] + 'T00:00:00');
    return `${start.getMonth() + 1}/${start.getDate()} 〜 ${end.getMonth() + 1}/${end.getDate()}`;
  }, [weekDates]);

  // Calculate period date ranges
  const periodDateRange = useMemo(() => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date;
    let label = '';

    switch (activeTab) {
      case 'weekly': {
        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7) + periodWeekOffset * 7);
        startDate = new Date(monday);
        endDate = new Date(monday);
        endDate.setDate(monday.getDate() + 6);
        label = `${startDate.getMonth() + 1}/${startDate.getDate()}〜${endDate.getMonth() + 1}/${endDate.getDate()}`;
        break;
      }
      case 'monthly': {
        const targetMonth = new Date(today.getFullYear(), today.getMonth() + periodMonthOffset, 1);
        startDate = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);
        endDate = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
        label = `${targetMonth.getFullYear()}年${targetMonth.getMonth() + 1}月`;
        break;
      }
      case 'quarterly': {
        const currentQuarter = Math.floor(today.getMonth() / 3);
        const targetQuarter = currentQuarter + periodQuarterOffset;
        const targetYear = today.getFullYear() + Math.floor(targetQuarter / 4);
        const normalizedQuarter = ((targetQuarter % 4) + 4) % 4;
        const startMonth = normalizedQuarter * 3;
        startDate = new Date(targetYear, startMonth, 1);
        endDate = new Date(targetYear, startMonth + 3, 0);
        const endYear = endDate.getFullYear();
        label = `${startDate.getFullYear()}年${startDate.getMonth() + 1}月〜${endYear}年${endDate.getMonth() + 1}月`;
        break;
      }
      case 'yearly': {
        const targetYear = today.getFullYear() + periodYearOffset;
        startDate = new Date(targetYear, 0, 1);
        endDate = new Date(targetYear, 11, 31);
        label = `${targetYear}年`;
        break;
      }
      default:
        return { startDate: formatDateKey(today), endDate: formatDateKey(today), label: '' };
    }

    return {
      startDate: formatDateKey(startDate),
      endDate: formatDateKey(endDate),
      label,
    };
  }, [activeTab, periodWeekOffset, periodMonthOffset, periodQuarterOffset, periodYearOffset]);

  /* ---------- 現在の期間ラベルを計算（PDF用） ---------- */
  const currentPeriodLabels = useMemo(() => {
    const today = new Date();

    // 週間
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
    const weekEnd = new Date(monday);
    weekEnd.setDate(monday.getDate() + 6);
    const weeklyLabel = `${monday.getMonth() + 1}/${monday.getDate()}〜${weekEnd.getMonth() + 1}/${weekEnd.getDate()}`;

    // 月間
    const monthlyLabel = `${today.getFullYear()}年${today.getMonth() + 1}月`;

    // 3ヶ月
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const qStartMonth = currentQuarter * 3;
    const qStart = new Date(today.getFullYear(), qStartMonth, 1);
    const qEnd = new Date(today.getFullYear(), qStartMonth + 3, 0);
    const quarterlyLabel = `${qStart.getFullYear()}年${qStart.getMonth() + 1}月〜${qEnd.getFullYear()}年${qEnd.getMonth() + 1}月`;

    // 年間
    const yearlyLabel = `${today.getFullYear()}年`;

    return { weekly: weeklyLabel, monthly: monthlyLabel, quarterly: quarterlyLabel, yearly: yearlyLabel };
  }, []);

  /* ---------- Data loading ---------- */
  useEffect(() => {
    if (user && weekDates.length > 0 && activeTab === 'daily') {
      loadGoals();
    }
  }, [user, weekDates, activeTab]);

  useEffect(() => {
    if (user && activeTab !== 'daily') {
      loadPeriodGoals(activeTab);
    }
  }, [user, activeTab, periodWeekOffset, periodMonthOffset, periodQuarterOffset, periodYearOffset]);

  const loadGoals = async () => {
    if (weekDates.length === 0) return;
    setLoading(true);
    try {
      const data = await fetchGoalsByRange(weekDates[0], weekDates[6]);
      setGoals(data);
    } catch (e) {
      console.error('Failed to load goals:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodGoals = async (type: GoalType) => {
    setPeriodLoading(true);
    try {
      const data = await fetchGoalsByType(type, periodDateRange.startDate, periodDateRange.endDate);
      setPeriodGoals(data);
    } catch (e) {
      console.error('Failed to load period goals:', e);
    } finally {
      setPeriodLoading(false);
    }
  };

  /* ---------- PDF Export ---------- */
  const handleExportPDF = useCallback(async () => {
    if (!user) return;
    setIsExporting(true);
    setExportError(null);
    try {
      // 現在の週の日別目標は既にgoalsに入っている
      // 各期間の目標を並行取得（現在期間）
      const today = new Date();

      // 週間
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
      const weekEnd = new Date(monday);
      weekEnd.setDate(monday.getDate() + 6);

      // 月間
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      // 3ヶ月
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const qStartMonth = currentQuarter * 3;
      const qStart = new Date(today.getFullYear(), qStartMonth, 1);
      const qEnd = new Date(today.getFullYear(), qStartMonth + 3, 0);

      // 年間
      const yearStart = new Date(today.getFullYear(), 0, 1);
      const yearEnd = new Date(today.getFullYear(), 11, 31);

      const fmt = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
      };

      const [weeklyGoals, monthlyGoals, quarterlyGoals, yearlyGoals] = await Promise.all([
        fetchGoalsByType('weekly', fmt(monday), fmt(weekEnd)),
        fetchGoalsByType('monthly', fmt(monthStart), fmt(monthEnd)),
        fetchGoalsByType('quarterly', fmt(qStart), fmt(qEnd)),
        fetchGoalsByType('yearly', fmt(yearStart), fmt(yearEnd)),
      ]);

      const exportData: ScheduleExportData = {
        username: username || '',
        dailyGoals: goals,
        weekDates,
        weekLabel,
        periodGoals: {
          weekly: weeklyGoals,
          monthly: monthlyGoals,
          quarterly: quarterlyGoals,
          yearly: yearlyGoals,
        },
        periodLabels: currentPeriodLabels,
      };

      await exportScheduleToPDF(exportData);
    } catch (e) {
      console.error('PDF export failed:', e);
      setExportError('PDFの生成に失敗しました。もう一度お試しください。');
      setTimeout(() => setExportError(null), 4000);
    } finally {
      setIsExporting(false);
    }
  }, [user, username, goals, weekDates, weekLabel, currentPeriodLabels]);

  /* ---------- Derived data ---------- */
  const goalsByDate = useMemo(() => {
    const map: Record<string, StudyGoal[]> = {};
    weekDates.forEach((d) => (map[d] = []));
    goals.forEach((g) => {
      if (map[g.date]) map[g.date].push(g);
    });
    Object.keys(map).forEach((d) => {
      map[d] = sortGoals(map[d], dailySortMode);
    });
    return map;
  }, [goals, weekDates, dailySortMode]);

  /* ---------- Handlers ---------- */
  const handleAddGoal = async (date: string) => {
    if (!newGoalText.trim()) return;
    try {
      const result = await addGoal(date, newGoalText.trim(), 'daily', newGoalDifficulty);
      if (result) {
        setGoals((prev) => [...prev, result]);
        setNewGoalText('');
        setNewGoalDifficulty(3);
        setAddingDate(null);
      }
    } catch (e) {
      console.error('Failed to add goal:', e);
    }
  };

  const handleAddPeriodGoal = async () => {
    if (!periodNewGoalText.trim()) return;
    const todayStr = formatDateKey(new Date());
    try {
      const result = await addGoal(
        todayStr,
        periodNewGoalText.trim(),
        activeTab as GoalType,
        periodNewDifficulty
      );
      if (result) {
        setPeriodGoals((prev) => [...prev, result]);
        setPeriodNewGoalText('');
        setShowPeriodAdd(false);
        setPeriodNewDifficulty(3);
      }
    } catch (e) {
      console.error('Failed to add period goal:', e);
    }
  };

  const triggerCelebration = (msg: string) => {
    setCelebrationMsg(msg);
    setShowCelebration(true);
  };

  const checkDailyFullCompletion = (updatedGoals: StudyGoal[], dateStr: string) => {
    const dayGoals = updatedGoals.filter((g) => g.date === dateStr);
    if (dayGoals.length === 0) return;
    const allDone = dayGoals.every((g) => g.is_completed);
    if (allDone) {
      setTimeout(() => {
        triggerCelebration(`${formatDateLabel(dateStr)}の日別目標をすべて達成！`);
      }, 600);
    }
  };

  const checkPeriodFullCompletion = (updatedGoals: StudyGoal[]) => {
    if (updatedGoals.length === 0) return;
    const allDone = updatedGoals.every((g) => g.is_completed);
    if (allDone) {
      const config = PERIOD_LABELS[activeTab];
      if (config && config.title) {
        setTimeout(() => {
          triggerCelebration(`${config.title}をすべて達成！`);
        }, 600);
      }
    }
  };

  const handleToggle = async (goal: StudyGoal) => {
    const newVal = !goal.is_completed;
    try {
      const success = await toggleGoalCompletion(goal.id, newVal);
      if (success) {
        const updatedGoal = {
          ...goal,
          is_completed: newVal,
          checked_at: newVal ? new Date().toISOString() : null,
        };
        if (goal.goal_type === 'daily') {
          const updatedGoals = goals.map((g) => (g.id === goal.id ? updatedGoal : g));
          setGoals(updatedGoals);
          if (newVal) {
            triggerCelebration(
              `「${goal.goal_text.length > 20 ? goal.goal_text.slice(0, 20) + '...' : goal.goal_text}」達成！`
            );
            checkDailyFullCompletion(updatedGoals, goal.date);
          }
        } else {
          const updatedGoals = periodGoals.map((g) => (g.id === goal.id ? updatedGoal : g));
          setPeriodGoals(updatedGoals);
          if (newVal) {
            triggerCelebration(
              `「${goal.goal_text.length > 20 ? goal.goal_text.slice(0, 20) + '...' : goal.goal_text}」達成！`
            );
            checkPeriodFullCompletion(updatedGoals);
          }
        }
      }
    } catch (e) {
      console.error('Failed to toggle goal:', e);
    }
  };

  const handleDelete = async (goal: StudyGoal) => {
    try {
      const success = await deleteGoal(goal.id);
      if (success) {
        if (goal.goal_type === 'daily') {
          setGoals((prev) => prev.filter((g) => g.id !== goal.id));
        } else {
          setPeriodGoals((prev) => prev.filter((g) => g.id !== goal.id));
        }
      }
    } catch (e) {
      console.error('Failed to delete goal:', e);
    }
  };

  const handleStartEdit = (goal: StudyGoal) => {
    setEditingGoalId(goal.id);
    setEditingText(goal.goal_text);
    setEditingDifficulty(goal.difficulty || 3);
  };

  const handleSaveEdit = async (goal: StudyGoal) => {
    if (!editingText.trim()) return;
    const textChanged = editingText.trim() !== goal.goal_text;
    const difficultyChanged = editingDifficulty !== (goal.difficulty || 3);
    if (!textChanged && !difficultyChanged) {
      setEditingGoalId(null);
      return;
    }
    try {
      const success = await updateGoal(goal.id, editingText.trim(), editingDifficulty);
      if (success) {
        const updatedGoal = {
          ...goal,
          goal_text: editingText.trim(),
          difficulty: editingDifficulty,
        };
        if (goal.goal_type === 'daily') {
          setGoals((prev) => prev.map((g) => (g.id === goal.id ? updatedGoal : g)));
        } else {
          setPeriodGoals((prev) => prev.map((g) => (g.id === goal.id ? updatedGoal : g)));
        }
      }
    } catch (e) {
      console.error('Failed to update goal:', e);
    } finally {
      setEditingGoalId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingGoalId(null);
    setEditingText('');
    setEditingDifficulty(3);
  };

  const handlePeriodNavigate = (direction: 'prev' | 'next') => {
    switch (activeTab) {
      case 'weekly':
        setPeriodWeekOffset((prev) => prev + (direction === 'next' ? 1 : -1));
        break;
      case 'monthly':
        setPeriodMonthOffset((prev) => prev + (direction === 'next' ? 1 : -1));
        break;
      case 'quarterly':
        setPeriodQuarterOffset((prev) => prev + (direction === 'next' ? 1 : -1));
        break;
      case 'yearly':
        setPeriodYearOffset((prev) => prev + (direction === 'next' ? 1 : -1));
        break;
    }
  };

  const handleResetPeriod = () => {
    switch (activeTab) {
      case 'weekly':
        setPeriodWeekOffset(0);
        break;
      case 'monthly':
        setPeriodMonthOffset(0);
        break;
      case 'quarterly':
        setPeriodQuarterOffset(0);
        break;
      case 'yearly':
        setPeriodYearOffset(0);
        break;
    }
  };

  const isPeriodCurrent = () => {
    switch (activeTab) {
      case 'weekly':
        return periodWeekOffset === 0;
      case 'monthly':
        return periodMonthOffset === 0;
      case 'quarterly':
        return periodQuarterOffset === 0;
      case 'yearly':
        return periodYearOffset === 0;
      default:
        return true;
    }
  };

  /* ---------- Render ---------- */
  if (authLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 animate-pulse h-32"></div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-full mx-auto mb-3">
              <i className="ri-calendar-check-line text-xl text-white"></i>
            </div>
            <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-1">学習スケジュール &amp; 目標設定</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              日々の学習目標を設定し、達成度をチェックできます。<br />
              ご利用にはログインが必要です。
            </p>
            <button
              type="button"
              onClick={() => setShowAuth(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-login-box-line text-base"></i>
              ログインして利用する
            </button>
          </div>
        </div>
        {showAuth && (
          <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => setShowAuth(false)} />
        )}
      </>
    );
  }

  const periodCompletedCount = periodGoals.filter((g) => g.is_completed).length;
  const periodTotalCount = periodGoals.length;

  return (
    <>
      <CelebrationOverlay
        show={showCelebration}
        message={celebrationMsg}
        onDone={() => setShowCelebration(false)}
      />
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl shrink-0">
                <i className="ri-calendar-check-line text-lg text-white"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-gray-800 dark:text-white">学習スケジュール</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500">日々の目標から年間目標まで管理</p>
              </div>
            </div>
            {/* PDF Export Button */}
            <div className="flex flex-col items-end gap-1 shrink-0">
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExporting}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isExporting
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white hover:from-teal-600 hover:to-emerald-700 shadow-sm hover:shadow-md'
                }`}
                title="学習スケジュールをPDFでダウンロード"
              >
                {isExporting ? (
                  <>
                    <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>生成中...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-file-pdf-line text-sm"></i>
                    <span>PDFで共有</span>
                  </>
                )}
              </button>
              {exportError && (
                <p className="text-xs text-red-500 dark:text-red-400">{exportError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Tab selector */}
        <div className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
          <div className="px-4 py-2.5 overflow-x-auto no-scrollbar">
            <div className="inline-flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-full p-1 min-w-max">
              {TAB_CONFIG.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.key);
                    setShowPeriodAdd(false);
                    setPeriodNewGoalText('');
                    setPeriodNewDifficulty(3);
                    setEditingGoalId(null);
                  }}
                  className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-white dark:bg-gray-600 text-teal-700 dark:text-teal-300 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Daily tab content */}
        {activeTab === 'daily' && (
          <>
            {/* Week navigation */}
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setWeekOffset((p) => p - 1)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg text-gray-500 dark:text-gray-400"></i>
              </button>
              <div className="text-center">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{weekLabel}</span>
                {weekOffset !== 0 && (
                  <button
                    type="button"
                    onClick={() => setWeekOffset(0)}
                    className="ml-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 cursor-pointer whitespace-nowrap"
                  >
                    今週に戻る
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setWeekOffset((p) => p + 1)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg text-gray-500 dark:text-gray-400"></i>
              </button>
            </div>

            {/* Sort selector for daily */}
            <div className="px-5 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                <i className="ri-sort-desc text-sm mr-0.5"></i>並び替え:
              </span>
              <div className="flex gap-1">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setDailySortMode(opt.key)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      dailySortMode === opt.key
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <i className={`${opt.icon} text-xs`}></i>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Schedule content */}
            {loading ? (
              <div className="p-6 text-center">
                <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {weekDates.map((dateStr) => {
                  const dayGoals = goalsByDate[dateStr] || [];
                  const isTodayDate = isToday(dateStr);
                  const isPastDate = isPast(dateStr);
                  const completedCount = dayGoals.filter((g) => g.is_completed).length;
                  const totalCount = dayGoals.length;
                  const allDone = totalCount > 0 && completedCount === totalCount;
                  const isAdding = addingDate === dateStr;

                  const totalWeight = dayGoals.reduce((sum, g) => sum + (g.difficulty || 3), 0);
                  const completedWeight = dayGoals
                    .filter((g) => g.is_completed)
                    .reduce((sum, g) => sum + (g.difficulty || 3), 0);
                  const dayProgress = totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;

                  return (
                    <div key={dateStr} className={`px-5 py-3 ${isTodayDate ? 'bg-teal-50/30 dark:bg-teal-900/10' : ''}`}>
                      {/* Date header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              isTodayDate
                                ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                                : isPastDate
                                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            {formatDateLabel(dateStr)}
                          </span>
                          {isTodayDate && (
                            <span className="text-xs text-teal-600 dark:text-teal-400 font-medium">今日</span>
                          )}
                          {totalCount > 0 && (
                            <span className={`text-xs ${allDone ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
                              {allDone ? (
                                <>
                                  <i className="ri-checkbox-circle-fill mr-0.5"></i>達成
                                </>
                              ) : (
                                `${completedCount}/${totalCount}`
                              )}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setAddingDate(isAdding ? null : dateStr);
                            setNewGoalText('');
                            setNewGoalDifficulty(3);
                          }}
                          className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                          title="目標を追加"
                        >
                          <i className={`${isAdding ? 'ri-close-line' : 'ri-add-line'} text-sm text-gray-400`}></i>
                        </button>
                      </div>

                      {/* Goals list */}
                      {dayGoals.length > 0 ? (
                        <div className="space-y-1.5 ml-1">
                          {dayGoals.map((goal) => {
                            const isEditing = editingGoalId === goal.id;
                            const difficulty = goal.difficulty || 3;
                            return (
                              <div
                                key={goal.id}
                                className="flex items-center gap-2 group hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:-translate-y-0.5 hover:shadow-sm rounded-lg px-2 py-1 -mx-2 transition-all duration-200"
                              >
                                <button
                                  type="button"
                                  onClick={() => handleToggle(goal)}
                                  className={`w-5 h-5 flex items-center justify-center rounded-md border transition-all shrink-0 cursor-pointer ${
                                    goal.is_completed
                                      ? 'bg-gradient-to-br from-teal-400 to-emerald-500 border-teal-400'
                                      : 'border-gray-300 dark:border-gray-600 hover:border-teal-400'
                                  }`}
                                >
                                  {goal.is_completed && <i className="ri-check-line text-xs text-white"></i>}
                                </button>
                                {isEditing ? (
                                  <div className="flex-1 space-y-1.5">
                                    <div className="flex items-center gap-1.5">
                                      <input
                                        type="text"
                                        value={editingText}
                                        onChange={(e) => setEditingText(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSaveEdit(goal);
                                          if (e.key === 'Escape') handleCancelEdit();
                                        }}
                                        className="flex-1 px-2 py-1 border border-teal-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white dark:bg-gray-700 dark:text-white dark:border-teal-600"
                                        autoFocus
                                        maxLength={100}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleSaveEdit(goal)}
                                        className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-500 hover:bg-teal-600 transition-colors cursor-pointer"
                                      >
                                        <i className="ri-check-line text-xs text-white"></i>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                                      >
                                        <i className="ri-close-line text-xs text-gray-400"></i>
                                      </button>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs text-gray-400 shrink-0">難易度:</span>
                                      <DifficultySelector
                                        value={editingDifficulty}
                                        onChange={setEditingDifficulty}
                                        size="xs"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <span
                                      className={`text-xs flex-1 ${
                                        goal.is_completed ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-200'
                                      }`}
                                    >
                                      {goal.goal_text}
                                    </span>
                                    <div className="flex shrink-0" title={`難易度: ${difficulty}`}>
                                      {[...Array(difficulty)].map((_, i) => (
                                        <i key={i} className="ri-star-fill text-xs text-amber-400"></i>
                                      ))}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleStartEdit(goal)}
                                      className="w-5 h-5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-all cursor-pointer"
                                      title="編集"
                                    >
                                      <i className="ri-pencil-line text-xs text-teal-500"></i>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(goal)}
                                      className="w-5 h-5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all cursor-pointer"
                                      title="削除"
                                    >
                                      <i className="ri-delete-bin-line text-xs text-red-400"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        !isAdding && (
                          <div className="flex items-center gap-1.5 ml-1 py-1">
                            <i className="ri-lightbulb-line text-xs text-amber-400"></i>
                            <p className="text-xs text-amber-500 dark:text-amber-400">目標を設定して円滑に学習しよう</p>
                          </div>
                        )
                      )}

                      {/* Daily progress bar */}
                      {totalCount > 0 && (
                        <div className="mt-2 ml-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                              <i className="ri-bar-chart-box-line text-xs"></i>進捗（難易度加味）
                            </span>
                            <span className={`text-xs font-bold ${allDone ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {allDone && <i className="ri-checkbox-circle-fill mr-0.5"></i>}
                              {Math.round(dayProgress)}%
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-700 ease-out ${
                                allDone
                                  ? 'bg-gradient-to-r from-teal-400 to-emerald-500'
                                  : 'bg-gradient-to-r from-amber-400 to-orange-500'
                              }`}
                              style={{ width: `${dayProgress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Add goal input */}
                      {isAdding && (
                        <div className="mt-2 space-y-2">
                          <input
                            type="text"
                            value={newGoalText}
                            onChange={(e) => setNewGoalText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddGoal(dateStr);
                            }}
                            placeholder="目標を入力..."
                            className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 dark:text-white"
                            autoFocus
                            maxLength={100}
                          />
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">難易度:</span>
                            <DifficultySelector value={newGoalDifficulty} onChange={setNewGoalDifficulty} size="sm" />
                            <button
                              type="button"
                              onClick={() => handleAddGoal(dateStr)}
                              disabled={!newGoalText.trim()}
                              className="ml-auto px-3 py-1.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-xs font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              追加
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Period goal tabs */}
        {activeTab !== 'daily' && (
          <PeriodGoalPanel
            activeTab={activeTab}
            goals={periodGoals}
            loading={periodLoading}
            showAdd={showPeriodAdd}
            newGoalText={periodNewGoalText}
            newDifficulty={periodNewDifficulty}
            editingGoalId={editingGoalId}
            editingText={editingText}
            editingDifficulty={editingDifficulty}
            sortMode={periodSortMode}
            periodLabel={periodDateRange.label}
            isPeriodCurrent={isPeriodCurrent()}
            onSortModeChange={setPeriodSortMode}
            onToggleAdd={() => {
              setShowPeriodAdd((p) => !p);
              setPeriodNewGoalText('');
              setPeriodNewDifficulty(3);
            }}
            onChangeText={setPeriodNewGoalText}
            onChangeDifficulty={setPeriodNewDifficulty}
            onAdd={handleAddPeriodGoal}
            onToggle={handleToggle}
            onDelete={handleDelete}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditTextChange={setEditingText}
            onEditDifficultyChange={setEditingDifficulty}
            onNavigate={handlePeriodNavigate}
            onReset={handleResetPeriod}
            completedCount={periodCompletedCount}
            totalCount={periodTotalCount}
          />
        )}
      </div>
    </>
  );
}

/* -------------------------------------------------
   PeriodGoalPanel Component
--------------------------------------------------- */
function PeriodGoalPanel({
  activeTab,
  goals,
  loading,
  showAdd,
  newGoalText,
  newDifficulty,
  editingGoalId,
  editingText,
  editingDifficulty,
  sortMode,
  periodLabel,
  isPeriodCurrent,
  onSortModeChange,
  onToggleAdd,
  onChangeText,
  onChangeDifficulty,
  onAdd,
  onToggle,
  onDelete,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditTextChange,
  onEditDifficultyChange,
  onNavigate,
  onReset,
  completedCount,
  totalCount,
}: PeriodGoalPanelProps) {
  const config = PERIOD_LABELS[activeTab];
  if (!config) return null;

  const sortedGoals = sortGoals(goals, sortMode);

  const totalWeight = goals.reduce((sum, g) => sum + (g.difficulty || 3), 0);
  const completedWeight = goals.filter((g) => g.is_completed).reduce((sum, g) => sum + (g.difficulty || 3), 0);
  const weightedProgress = totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;

  return (
    <div>
      {/* Period navigation */}
      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <button
          type="button"
          onClick={() => onNavigate('prev')}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg text-gray-500 dark:text-gray-400"></i>
        </button>
        <div className="text-center">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{periodLabel}</span>
          {!isPeriodCurrent && (
            <button
              type="button"
              onClick={onReset}
              className="ml-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 cursor-pointer whitespace-nowrap"
            >
              {activeTab === 'weekly' && '今週に戻る'}
              {activeTab === 'monthly' && '今月に戻る'}
              {activeTab === 'quarterly' && '今期に戻る'}
              {activeTab === 'yearly' && '今年に戻る'}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => onNavigate('next')}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg text-gray-500 dark:text-gray-400"></i>
        </button>
      </div>

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center bg-gradient-to-br ${config.color} rounded-xl shrink-0`}>
              <i className={`${config.icon} text-lg text-white`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-gray-800 dark:text-white">{config.title}</h4>
                {totalCount > 0 && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      completedCount === totalCount ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {completedCount}/{totalCount}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">{config.description}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onToggleAdd}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            title="目標を追加"
          >
            <i className={`${showAdd ? 'ri-close-line' : 'ri-add-circle-line'} text-lg text-gray-400`}></i>
          </button>
        </div>

        {/* Sort selector */}
        {goals.length > 1 && (
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
              <i className="ri-sort-desc text-sm mr-0.5"></i>並び替え:
            </span>
            <div className="flex gap-1">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onSortModeChange(opt.key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortMode === opt.key
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <i className={`${opt.icon} text-xs`}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add input */}
        {showAdd && (
          <div className="mb-4 space-y-2">
            <input
              type="text"
              value={newGoalText}
              onChange={(e) => onChangeText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onAdd();
              }}
              placeholder={`${config.title}を入力...`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 dark:text-white"
              autoFocus
              maxLength={200}
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">難易度:</span>
              <DifficultySelector value={newDifficulty} onChange={onChangeDifficulty} size="sm" />
              <button
                type="button"
                onClick={onAdd}
                disabled={!newGoalText.trim()}
                className="ml-auto px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-xs font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                追加
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="py-8 text-center">
            <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : goals.length === 0 ? (
          <div className="py-8 text-center">
            <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${config.color} rounded-full mx-auto mb-3 opacity-30`}>
              <i className={`${config.icon} text-xl text-white`}></i>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">{config.title}がまだ設定されていません</p>
            <button
              type="button"
              onClick={onToggleAdd}
              className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-0.5"></i>最初の目標を追加する
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedGoals.map((goal, index) => {
              const isEditing = editingGoalId === goal.id;
              const difficulty = goal.difficulty || 3;
              return (
                <div
                  key={goal.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 cursor-default group ${
                    goal.is_completed
                      ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-sm'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-teal-200 dark:hover:border-teal-700 hover:-translate-y-0.5 hover:shadow-md'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onToggle(goal)}
                    className={`w-5 h-5 mt-0.5 flex items-center justify-center rounded-md border transition-all shrink-0 cursor-pointer ${
                      goal.is_completed
                        ? 'bg-gradient-to-br from-teal-400 to-emerald-500 border-teal-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-teal-400'
                    }`}
                  >
                    {goal.is_completed && <i className="ri-check-line text-xs text-white"></i>}
                  </button>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            value={editingText}
                            onChange={(e) => onEditTextChange(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') onSaveEdit(goal);
                              if (e.key === 'Escape') onCancelEdit();
                            }}
                            className="flex-1 px-2 py-1 border border-teal-300 dark:border-teal-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white dark:bg-gray-700 dark:text-white"
                            autoFocus
                            maxLength={200}
                          />
                          <button
                            type="button"
                            onClick={() => onSaveEdit(goal)}
                            className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-500 hover:bg-teal-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-check-line text-xs text-white"></i>
                          </button>
                          <button
                            type="button"
                            onClick={onCancelEdit}
                            className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-xs text-gray-400"></i>
                          </button>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">難易度:</span>
                          <DifficultySelector value={editingDifficulty} onChange={onEditDifficultyChange} size="xs" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-2 mb-1">
                          <span
                            className={`text-xs leading-relaxed flex-1 ${goal.is_completed ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-700 dark:text-gray-200'}`}
                          >
                            {goal.goal_text}
                          </span>
                          <div className="flex shrink-0" title={`難易度: ${difficulty}`}>
                            {[...Array(difficulty)].map((_, i) => (
                              <i key={i} className="ri-star-fill text-xs text-amber-400"></i>
                            ))}
                          </div>
                        </div>
                        {goal.is_completed && goal.checked_at && (
                          <p className="text-xs text-gray-300 dark:text-gray-600 mt-0.5">
                            <i className="ri-check-double-line mr-0.5"></i>
                            {new Date(goal.checked_at).toLocaleDateString('ja-JP', {
                              month: 'short',
                              day: 'numeric',
                            })}
                            に達成
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  {!isEditing && (
                    <>
                      <span className="text-xs text-gray-300 dark:text-gray-600 font-medium shrink-0 mt-0.5">#{index + 1}</span>
                      <button
                        type="button"
                        onClick={() => onStartEdit(goal)}
                        className="w-5 h-5 mt-0.5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-all cursor-pointer shrink-0"
                        title="編集"
                      >
                        <i className="ri-pencil-line text-xs text-teal-500"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(goal)}
                        className="w-5 h-5 mt-0.5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all cursor-pointer shrink-0"
                        title="削除"
                      >
                        <i className="ri-delete-bin-line text-xs text-red-400"></i>
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Weighted Progress Chart */}
        {totalCount > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">目標進捗度（難易度加味）</span>
              <span className="text-xs font-bold text-gray-700 dark:text-gray-200">{Math.round(weightedProgress)}%</span>
            </div>
            <div className="w-full h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative">
              <div
                className={`h-full bg-gradient-to-r ${config.color} rounded-full transition-all duration-700 ease-out`}
                style={{ width: `${weightedProgress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
              難易度の合計: {totalWeight} / 達成済み: {completedWeight}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------
   Supporting components & types
--------------------------------------------------- */
interface PeriodGoalPanelProps {
  activeTab: ScheduleTab;
  goals: StudyGoal[];
  loading: boolean;
  showAdd: boolean;
  newGoalText: string;
  newDifficulty: number;
  editingGoalId: string | null;
  editingText: string;
  editingDifficulty: number;
  sortMode: SortMode;
  periodLabel: string;
  isPeriodCurrent: boolean;
  onSortModeChange: (mode: SortMode) => void;
  onToggleAdd: () => void;
  onChangeText: (text: string) => void;
  onChangeDifficulty: (difficulty: number) => void;
  onAdd: () => void;
  onToggle: (goal: StudyGoal) => void;
  onDelete: (goal: StudyGoal) => void;
  onStartEdit: (goal: StudyGoal) => void;
  onSaveEdit: (goal: StudyGoal) => void;
  onCancelEdit: () => void;
  onEditTextChange: (text: string) => void;
  onEditDifficultyChange: (difficulty: number) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  onReset: () => void;
  completedCount: number;
  totalCount: number;
}

interface DifficultySelectorProps {
  value: number;
  onChange: (value: number) => void;
  size?: 'xs' | 'sm' | 'md';
}

function DifficultySelector({ value, onChange, size = 'sm' }: DifficultySelectorProps) {
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
  };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((level) => (
        <button
          key={level}
          type="button"
          onClick={() => onChange(level)}
          className={`${sizeClasses[size]} transition-colors cursor-pointer ${
            level <= value ? 'text-amber-400 hover:text-amber-500' : 'text-gray-300 dark:text-gray-600 hover:text-gray-400'
          }`}
          title={`難易度 ${level}`}
        >
          <i className="ri-star-fill"></i>
        </button>
      ))}
    </div>
  );
}